import React, { useMemo, useState } from "react";
import {
  Input, Button, Spin, Typography, Card, Tag, Progress, Space, message, Empty,
  Segmented, Row, Col, Statistic, Table, Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ClockCircleOutlined, ReloadOutlined, SafetyCertificateOutlined, RobotOutlined, BulbOutlined, InfoCircleOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import {
  runSearch, getResults, getSearchStatus, getCandidates, getCandidateStatus,
  riskColor, parseTimecodes, JudgeResultRow, Candidate,
} from "./api";
import EpisodeDrawer from "./EpisodeDrawer";

const { Title, Paragraph, Text } = Typography;

const EXAMPLES = ["mass shooting", "flood disaster", "suicide", "drug abuse", "terrorism"];

const riskHex = (risk?: string): string =>
  risk === "high" ? "#ff4d4f" : risk === "medium" ? "#faad14" : "#52c41a";

const Hero = styled.div`
  background: linear-gradient(135deg, #47cb38 0%, #2f9e44 100%);
  border-radius: 14px;
  padding: 32px 32px 28px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(71, 203, 56, 0.25);
  .ant-input-search .ant-input,
  .ant-input-search .ant-input-affix-wrapper {
    border-radius: 8px 0 0 8px;
  }
`;

const Chip = styled(Tag)`
  cursor: pointer;
  border: none;
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  padding: 2px 12px;
  border-radius: 14px;
  transition: background 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.32); }
`;

const ResultCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 10px;
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover { box-shadow: 0 6px 22px rgba(0, 0, 0, 0.1); transform: translateY(-1px); }
`;

type RiskFilter = "all" | "high" | "medium" | "low";
type View = "candidates" | "ai";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const pollUntilDone = async (searchId: string, onTotal: (total: number) => void) => {
  const maxAttempts = 200; // ~16 minutes at 5s intervals
  for (let i = 0; i < maxAttempts; i++) {
    await sleep(5000);
    let s: { status: string; total?: number; error?: string };
    try {
      s = await getSearchStatus(searchId);
    } catch {
      continue;
    }
    if (s.total) onTotal(s.total);
    if (s.status === "done") return;
    if (s.status === "error") throw new Error(s.error || "Search failed");
  }
  throw new Error("Search timed out — please try again.");
};

/** Poll an async candidate build until done; resolves with the candidates. */
const pollCandidatesDone = async (candidateSearchId: string): Promise<Candidate[]> => {
  const maxAttempts = 120; // ~10 minutes at 5s intervals
  for (let i = 0; i < maxAttempts; i++) {
    await sleep(5000);
    let s: { status: string; candidates?: Candidate[]; error?: string };
    try {
      s = await getCandidateStatus(candidateSearchId);
    } catch {
      continue;
    }
    if (s.status === "done") return s.candidates ?? [];
    if (s.status === "error") throw new Error(s.error || "Search failed");
  }
  throw new Error("Search timed out — please try again.");
};

/** Tags describing which of the 4 search angles surfaced a candidate. */
const sourceTags = (s: Candidate["sources"]) => {
  const tags: Array<{ label: string; color: string }> = [];
  if (s.semanticChunk != null) tags.push({ label: "Semantic · Caption", color: "geekblue" });
  if (s.keywordChunk != null) tags.push({ label: "Keyword · Caption", color: "blue" });
  if (s.semanticMeta != null) tags.push({ label: "Semantic · Meta", color: "purple" });
  if (s.keywordMeta != null) tags.push({ label: "Keyword · Meta", color: "cyan" });
  return tags;
};

const SensitiveSearchPage: React.FC = () => {
  const [topic, setTopic] = useState("");
  const [lastTopic, setLastTopic] = useState("");
  const [searched, setSearched] = useState(false);
  const [view, setView] = useState<View>("candidates");

  // Step 1 — candidates (cheap, no LLM)
  const [candLoading, setCandLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candCached, setCandCached] = useState(false);

  // Step 2 — AI judge (expensive, opt-in)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStage, setAiStage] = useState("");
  const [results, setResults] = useState<JudgeResultRow[]>([]);
  const [cached, setCached] = useState(false);
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("all");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRefId, setDrawerRefId] = useState<string | null>(null);
  const [drawerResult, setDrawerResult] = useState<JudgeResultRow | null>(null);

  // Step 1: assemble the candidate list. Fast — no AI. Uses the topic cache
  // unless forceRefresh is set (the "Run fresh" button).
  const doSearch = async (value: string, forceRefresh = false) => {
    const t = value.trim();
    if (!t) {
      message.warning("Enter a topic to search");
      return;
    }
    setTopic(t);
    setLastTopic(t);
    setSearched(true);
    setView("candidates");
    setResults([]);
    setCandidates([]);
    setRiskFilter("all");
    setCandLoading(true);
    try {
      const res = await getCandidates(t, forceRefresh);
      setCandCached(Boolean(res.cached));
      // Cache hit returns candidates directly; a fresh build runs in the
      // background — poll until it finishes.
      if (res.status === "done" && res.candidates) {
        setCandidates(res.candidates);
      } else if (res.candidateSearchId) {
        const built = await pollCandidatesDone(res.candidateSearchId);
        setCandidates(built);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || "Search failed");
    } finally {
      setCandLoading(false);
    }
  };

  // Step 2: run the LLM judge on the candidates (opt-in / on demand).
  const runAI = async (forceRefresh = false) => {
    if (!lastTopic) return;
    setAiLoading(true);
    setView("ai");
    setResults([]);
    setRiskFilter("all");
    setAiStage("Starting AI review...");
    try {
      const res = await runSearch(lastTopic, forceRefresh);
      setCached(Boolean(res?.cached));
      if (res.status !== "done" && !res.cached) {
        setAiStage("AI is reviewing episodes...");
        await pollUntilDone(res.searchId, (total) => setAiStage(`AI is reviewing ${total} episodes...`));
      }
      const { results: rows } = await getResults(res.searchId);
      setResults(rows);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || "AI review failed");
    } finally {
      setAiLoading(false);
      setAiStage("");
    }
  };

  const counts = useMemo(
    () => ({
      high: results.filter((r) => r.RISK_LEVEL === "high").length,
      medium: results.filter((r) => r.RISK_LEVEL === "medium").length,
      low: results.filter((r) => r.RISK_LEVEL === "low").length,
    }),
    [results]
  );

  const visible = useMemo(
    () => (riskFilter === "all" ? results : results.filter((r) => r.RISK_LEVEL === riskFilter)),
    [results, riskFilter]
  );

  const openCandidate = (refId: string) => {
    setDrawerRefId(refId);
    setDrawerResult(null);
    setDrawerOpen(true);
  };
  const openResult = (row: JudgeResultRow) => {
    setDrawerRefId(row.REF_ID);
    setDrawerResult(row);
    setDrawerOpen(true);
  };

  const candidateColumns: ColumnsType<Candidate> = [
    {
      title: "Rank",
      key: "rank",
      width: 70,
      align: "center",
      // Rank by candidate Score (backend returns them sorted finalScore desc).
      render: (_, r) => candidates.findIndex((c) => c.refId === r.refId) + 1,
    },
    // {
    //   title: "Score",
    //   dataIndex: "score",
    //   key: "score",
    //   width: 80,
    //   sorter: (a, b) => a.finalScore - b.finalScore,
    //   defaultSortOrder: "descend",
    //   render: (v: number) => <Tag color="green">{v}</Tag>,
    // },
    {
      title: "REF ID",
      dataIndex: "refId",
      key: "refId",
      width: 130,
      render: (v: string) => <Text strong style={{ whiteSpace: "nowrap" }}>{v}</Text>,
    },
    {
      title: "Matched via",
      key: "sources",
      width: 320,
      render: (_, r) => (
        <Space size={[4, 4]} wrap>
          {sourceTags(r.sources).map((t) => (
            <Tag key={t.label} color={t.color} style={{ marginInlineEnd: 0 }}>
              {t.label}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Caption file",
      key: "captionFile",
      width: 260,
      // Truncate with "…"; full name shows on hover (showTitle:false avoids the
      // native title so only our Tooltip appears).
      ellipsis: { showTitle: false },
      render: (_, r) => {
        const file = r.chunks.find((c) => c.fileName)?.fileName;
        return file ? (
          <Tooltip title={file}>
            <span style={{ fontSize: 12 }}>{file}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, r) => (
        <Button size="small" onClick={() => openCandidate(r.refId)}>
          View details
        </Button>
      ),
    },
  ];

  // Expanded row: the caption segments that triggered the match.
  const expandedRow = (r: Candidate) => {
    if (!r.chunks?.length) {
      return <Text type="secondary">Matched on metadata only — no triggering caption segments.</Text>;
    }
    return (
      <div style={{ paddingLeft: 8 }}>
        {r.chunks.map((c, i) => (
          <div key={`${c.timecodeStart}-${i}`} style={{ marginBottom: 8 }}>
            <Space size={[6, 0]} wrap>
              <Tag color={c.source === "semantic" ? "geekblue" : "blue"}>{c.source}</Tag>
              <Text strong style={{ color: "#d48806" }}>
                <ClockCircleOutlined /> {c.timecodeStart}
              </Text>
              <Tag>match {(c.score * 100).toFixed(0)}</Tag>
              {c.fileName && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  📄 {c.fileName}
                </Text>
              )}
            </Space>
            <div>{c.chunkText}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1080, margin: "0 auto" }}>
      <Hero>
        <Title level={2} style={{ color: "#fff", marginBottom: 4 }}>
          <SafetyCertificateOutlined style={{ marginRight: 10 }} />
          Sensitive Topic Search
        </Title>
        {/* <Paragraph style={{ color: "rgba(255,255,255,0.88)", marginBottom: 20, fontSize: 15 }}>
          Step 1 builds the candidate list (fast). Step 2 runs the AI review on those candidates (slower).
        </Paragraph> */}

        <Input.Search
          size="large"
          placeholder='e.g. "mass shooting" or "flood disaster"'
          enterButton="Search"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onSearch={(value) => doSearch(value)}
          loading={candLoading}
          disabled={candLoading}
          style={{ maxWidth: 620 }}
        />

        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
          <BulbOutlined style={{ color: "rgba(255,255,255,0.9)" }} />
          <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 13 }}>
            Tip: describe the scenario in plain words — skip names, places &amp; dates.
          </Text>
          <Tooltip
            color="#1f1f1f"
            title={
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                <div><strong>For the most relevant results:</strong></div>
                <div>• Describe what happens, not the news headline</div>
                <div>• Remove people / place / date names — episodes don&apos;t contain them</div>
                <div>• Use everyday words, not legal/technical phrasing</div>
                <div>• Keep one core theme per search; run sub-themes separately</div>
                <div>• Try a couple of phrasings for important topics</div>
                <div style={{ marginTop: 6, opacity: 0.85 }}>
                  e.g. “affair with the nanny and a plot to kill his wife” — not “the Banfield trial in Fairfax”
                </div>
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: "rgba(255,255,255,0.9)", cursor: "help" }} />
          </Tooltip>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text style={{ color: "rgba(255,255,255,0.85)", marginRight: 8 }}>Try:</Text>
          <Space size={[8, 8]} wrap>
            {EXAMPLES.map((ex) => (
              <Chip key={ex} onClick={() => !candLoading && doSearch(ex)}>
                {ex}
              </Chip>
            ))}
          </Space>
        </div>
      </Hero>

      {candLoading && (
        <Card style={{ marginTop: 24, textAlign: "center", borderRadius: 12 }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16, marginBottom: 4, fontWeight: 500 }}>
            Step 1 of 2: Building candidate list...
          </Paragraph>
          <Text type="secondary">Running keyword + semantic search across episodes and captions.</Text>
        </Card>
      )}

      {!candLoading && searched && (
        <div style={{ marginTop: 24 }}>
          {/* Step toggle + actions */}
          <div
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}
          >
            <Segmented
              value={view}
              onChange={(v) => setView(v as View)}
              options={[
                { label: `Step 1: Candidates (${candidates.length})`, value: "candidates" },
                {
                  label: results.length ? `Step 2: AI Results (${results.length})` : "Step 2: AI Results",
                  value: "ai",
                },
              ]}
            />
            <Space>
              {view === "candidates" && candCached && <Tag color="blue">Cached candidates</Tag>}
              {view === "candidates" && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => doSearch(lastTopic, true)}
                  disabled={candLoading}
                >
                  Run fresh
                </Button>
              )}
              {view === "ai" && cached && <Tag color="blue">Cached result</Tag>}
              {view === "ai" && results.length > 0 && (
                <Button icon={<ReloadOutlined />} onClick={() => runAI(true)} disabled={aiLoading}>
                  Re-run fresh
                </Button>
              )}
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={() => runAI(false)}
                loading={aiLoading}
                disabled={candidates.length === 0}
              >
                Run AI review
              </Button>
            </Space>
          </div>

          {/* STEP 1 — candidates */}
          {view === "candidates" &&
            (candidates.length === 0 ? (
              <Card style={{ borderRadius: 12 }}>
                <Empty description="No candidate episodes for this topic" />
              </Card>
            ) : (
              <Card size="small" style={{ borderRadius: 12 }}>
                <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                  These {candidates.length} episodes were assembled by the 4-way search and would be sent to the AI.
                  Expand a row to see the caption segments that triggered inclusion.
                </Paragraph>
                <Table
                  rowKey="refId"
                  size="middle"
                  columns={candidateColumns}
                  dataSource={candidates}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"] }}
                  expandable={{ expandedRowRender: expandedRow }}
                />
              </Card>
            ))}

          {/* STEP 2 — AI results */}
          {view === "ai" &&
            (aiLoading ? (
              <Card style={{ textAlign: "center", borderRadius: 12 }}>
                <Spin size="large" />
                <Paragraph style={{ marginTop: 16, marginBottom: 4, fontWeight: 500 }}>{aiStage}</Paragraph>
                <Text type="secondary">This can take 2–5 minutes. Please keep this tab open.</Text>
              </Card>
            ) : results.length === 0 ? (
              <Card style={{ borderRadius: 12 }}>
                <Empty description="No AI review yet — click “Run AI review” to score these candidates." />
              </Card>
            ) : (
              <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={6}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Statistic title="Episodes flagged" value={results.length} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Statistic title="High risk" value={counts.high} valueStyle={{ color: riskHex("high") }} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Statistic title="Medium" value={counts.medium} valueStyle={{ color: riskHex("medium") }} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Statistic title="Low" value={counts.low} valueStyle={{ color: riskHex("low") }} />
                    </Card>
                  </Col>
                </Row>

                <Segmented
                  style={{ marginBottom: 16 }}
                  value={riskFilter}
                  onChange={(v) => setRiskFilter(v as RiskFilter)}
                  options={[
                    { label: `All (${results.length})`, value: "all" },
                    { label: `High (${counts.high})`, value: "high" },
                    { label: `Medium (${counts.medium})`, value: "medium" },
                    { label: `Low (${counts.low})`, value: "low" },
                  ]}
                />

                {visible.map((r) => {
                  const tcs = parseTimecodes(r.TIMECODES);
                  return (
                    <ResultCard key={r.ID} style={{ borderLeft: `4px solid ${riskHex(r.RISK_LEVEL)}` }}>
                      <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
                        <Progress
                          type="circle"
                          size={64}
                          percent={Number(r.SCORE) || 0}
                          strokeColor={riskHex(r.RISK_LEVEL)}
                          format={(p) => <span style={{ fontSize: 16, fontWeight: 600 }}>{p}</span>}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Space wrap size={[8, 4]} style={{ marginBottom: 6 }}>
                            <Text strong style={{ fontSize: 15 }}>
                              {r.TITLE || r.REF_ID}
                            </Text>
                            <Tag color={riskColor(r.RISK_LEVEL)}>{(r.RISK_LEVEL || "").toUpperCase()}</Tag>
                            {(r.SEASON != null || r.EPISODE_NUMBER != null) && (
                              <Tag>
                                S{r.SEASON ?? "?"} E{r.EPISODE_NUMBER ?? "?"}
                              </Tag>
                            )}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {r.REF_ID}
                            </Text>
                          </Space>
                          <Paragraph style={{ marginBottom: tcs.length ? 8 : 0 }} ellipsis={{ rows: 2, tooltip: r.REASON }}>
                            {r.REASON}
                          </Paragraph>
                          {tcs.length > 0 && (
                            <Space size={[6, 6]} wrap>
                              {tcs.slice(0, 5).map((tc) => (
                                <Tag key={tc} icon={<ClockCircleOutlined />} color="gold">
                                  {tc}
                                </Tag>
                              ))}
                            </Space>
                          )}
                        </div>
                        <Tooltip title="Open episode details">
                          <Button type="primary" ghost onClick={() => openResult(r)}>
                            View details
                          </Button>
                        </Tooltip>
                      </div>
                    </ResultCard>
                  );
                })}
              </>
            ))}
        </div>
      )}

      <EpisodeDrawer
        open={drawerOpen}
        refId={drawerRefId}
        result={drawerResult}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
};

export default SensitiveSearchPage;
